/**
 * Handle the error response from creating a Stripe Token.
 * Returns an object of field errors to be handled by our custom
 * form abstraction.
 *
 * NOTE: The form field names must match the object properties returned by
 * this function
 */
export const parseStripeTokenError = (err) => {
  const defaultResponse = {
    ccNumber: err.message,
    ccExpiration: err.message,
    ccCVC: err.message,
  };

  if (err.type !== 'validation_error') {
    return defaultResponse;
  }

  switch (err.code) {
    case 'invalid_cvc':
    case 'incomplete_cvc':
      return {
        ccCVC: err.message,
      };
    case 'invalid_expiry_month':
    case 'invalid_expiry_year':
    case 'invalid_expiry_year_past':
    case 'incomplete_expiry':
      return {
        ccExpiration: err.message,
      };
    case 'invalid_number':
    case 'incomplete_number':
      return {
        ccNumber: err.message,
      };
    case 'postal_code_invalid':
    case 'incorrect_zip':
    case 'incorrect_address':
      return {
        ccZip: err.message,
      };
    default:
      return defaultResponse;
  }
};

/**
 * Handle the error message returned by our API when creating
 * a Stripe payment. Returns an object of field errors to be
 * handled by our custom form abstraction.
 *
 * NOTE: The form field names must match the object properties returned by
 * this function
 */
export const parseStripeApiError = (response) => {
  switch (response.detail.errorInfo.code) {
    case 'incorrect_cvc':
      return {
        ccCVC: response.detail.errorInfo.message,
      };
    case 'insufficient_funds':
    case 'card_declined':
    case 'processing_error':
    default:
      return {
        ccNumber: response.detail.errorInfo.message,
        ccExpiration: response.detail.errorInfo.message,
        ccCVC: response.detail.errorInfo.message,
      };
  }
};

/**
 * Stripe <PaymentElement /> methods
 */

/**
 * Handles elements validation by calling submit() before sending payment data
 */
export const handleValidation = async (elements, showFlashMessage) => {
  const { error: submitError } = await elements.submit();
  if (submitError) {
    showFlashMessage(submitError.message);
    return;
  }
};

/**
 * Use the clientSecret and Elements instance to confirm the payment.
 * If using a savedPaymentMethod, do not include 'elements' since no form
 */
export const confirmPayment = async (
  stripe,
  elements,
  clientSecret,
  confirmParams
) => {
  return await stripe.confirmPayment({
    elements: confirmParams.payment_method ? undefined : elements,
    clientSecret,
    confirmParams,
    redirect: 'if_required',
  });
};

export const confirmSetup = async (
  stripe,
  elements,
  clientSecret,
  confirmParams
) => {
  return await stripe.confirmSetup({
    elements: confirmParams.payment_method ? undefined : elements,
    clientSecret,
    confirmParams,
    redirect: 'if_required',
  });
};

/**
 * Handles the stripe call to create a payment method.
 * This is for when a user needs to add a payment method.
 */
export const createPaymentMethodElements = async (stripe, elements, params) => {
  return await stripe.createPaymentMethod({ elements, params });
};

/**
 * Stripe Card elements methods
 */

/**
 * Handles the stripe call to confirm a card payment in UserCreationTool
 */
export const confirmCardPayment = async (
  stripe,
  clientSecret,
  paymentMethod
) => {
  return await stripe.confirmCardPayment(clientSecret, paymentMethod);
};

export const PaymentTypes = {
  CARD: 'card',
  APPLE_PAY: 'apple_pay',
  GOOGLE_PAY: 'google_pay',
};

export const useIsAppleOrGooglePay = (source) => {
  if (!source) {
    return false;
  }

  return [PaymentTypes.APPLE_PAY, PaymentTypes.GOOGLE_PAY].includes(
    source.toLowerCase()
  );
};
